import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/app/providers/auth-context'
import type { StorefrontIdentity } from '@/features/clubs/storefront'
import { AppDrawer } from './AppDrawer'
import { PublicAccountMenu } from './PublicAccountMenu'
import { HamburgerIcon } from './PublicHeader'
import { StorefrontClubSwitcher } from './StorefrontClubSwitcher'
import styles from './PublicHeader.module.css'
import storefront from './StorefrontHeader.module.css'

/**
 * THE CLUB'S OWN TOP BAR (Club Storefront, brick 1) — the club's mark and name over the
 * one thing a visitor still needs from us: a way into their account.
 *
 * WHAT IS DELIBERATELY ABSENT: the wordmark for The Racket Center, the Product /
 * Pricing / Solutions / Guides / About menu, the "Join free" signup CTA, and the "Go to
 * workspace" button. A club pays for this page so that it does not host a menu of our
 * pricing and a search box for its competitors.
 *
 * WHAT STAYS, AND WHY: signing in. A player has to be signed in to book a court, so the
 * door has to be here — but it is dressed as the club's door. It returns the player to
 * this club's page rather than to the platform, and it never offers a signup CTA.
 *
 * AND, SINCE 2026-09-07, A WAY OUT (owner: "меню слева совсем нету"). That minimalism
 * had gone one step too far: a signed-in player who reached a storefront could not
 * navigate anywhere at all — no hamburger, no rail, nothing. The drawer comes back
 * carrying ONLY the app list (APP_NAV_ITEMS, the one menu the owner kept), through
 * the same AppDrawer the workspace uses; `navOnly` drops the marketing group, and the
 * `brand` prop puts the CLUB's name in the drawer head where the platform's wordmark
 * would otherwise sit. Half the minimalism survives intact — nothing in that drawer
 * names or sells The Racket Center.
 *
 * AND SINCE 2026-09-07, FOR A SIGNED-OUT VISITOR TOO. The menu first came back gated on
 * `isAuthed`, for two stated reasons. The second one was load-bearing and has since
 * expired: the list led with the match board at `/play/games`, behind RequireAuth, so a
 * stranger tapping the first row would have walked into a bare sign-in wall. 2a979c2f
 * pointed that row at `/play` — the auth-aware door (signed in → the board, signed out →
 * `/clubs`) — and there is no wall left to walk into; all five rows are public. The first
 * reason, that a stranger who followed a link to one club is not owed a tour of the
 * platform, was a judgement call, and the owner has now made the opposite one: he opened
 * his own club's link in incognito and found no way to navigate at all.
 *
 * SO THE HAMBURGER IS NOT GATED ON ANYTHING — not on the signed state, and not on
 * `checking` either. It renders from the first paint and never moves, so nothing pops
 * into the bar as POST /auth/refresh resolves. What the drawer holds does not move
 * either: `navOnly` draws the same one list in every state (see DrawerNavContent).
 *
 * WHAT A STRANGER STILL DOES NOT GET, and these gates are still right: the club
 * SWITCHER, which is a member's tool for hopping between clubs he belongs to and would
 * otherwise be this club's page naming its competitors (it refuses a guest itself, in
 * StorefrontClubSwitcher); and the ACCOUNT MENU, which has no account to open. They get
 * those replaced by the one thing they came for — the club's own Sign in.
 *
 * The bar reuses PublicHeader's own CSS module (the same fixed HUD bar, the same
 * --public-header-h the shell offsets its content by), so this is a different set of
 * things inside a bar the site already has, not a second bar to keep in step.
 */
export function StorefrontHeader({ club }: { club: StorefrontIdentity }) {
  const { state } = useAuth()
  const { pathname } = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const hamburgerRef = useRef<HTMLButtonElement | null>(null)
  const isAuthed = state === 'authenticated'
  const isChecking = state === 'checking'
  const clubHref = `/clubs/${encodeURIComponent(club.slug)}`

  // Close on route change, exactly as AppShell and PublicHeader do for their own menus.
  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  // The sign-in leg comes back HERE. A storefront that bounced its player onto the
  // platform's player hub after login would leak the marketplace through the one door
  // it has to keep open.
  const signInHref = `/login?returnTo=${encodeURIComponent(clubHref)}`

  return (
    <>
      <header className={styles.header}>
        <div className={`container ${styles.bar}`}>
          {/* Ungated on purpose — every viewer, every auth state. See the note above:
              the button that appears once auth settles is a button that was missing
              while it settled. */}
          <button
            type="button"
            ref={hamburgerRef}
            className={styles.hamburger}
            aria-label="Open navigation"
            aria-haspopup="dialog"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <HamburgerIcon />
          </button>
          <div className={storefront.brandGroup}>
            <Link to={clubHref} className={`${styles.brand} ${storefront.brand}`}>
              {club.logoUrl ? (
                <img className={storefront.logo} src={club.logoUrl} alt="" width={28} height={28} />
              ) : null}
              <span className={storefront.name}>{club.name}</span>
            </Link>
            {/* A member of THIS club who also belongs to another gets one chevron here
                and a hop to that club's storefront. Everyone else gets nothing — see
                features/clubs/storefrontClubSwitch.ts for who, and why so few. */}
            <StorefrontClubSwitcher club={club} />
          </div>

          <div className={styles.cluster}>
            {isChecking ? (
              <span className={styles.clusterSkeletonAvatar} aria-hidden="true" />
            ) : isAuthed ? (
              // THE SAME MENU AS EVERYWHERE ELSE, Workspace included (owner, 2026-09-06:
              // "always, like in the profile"). This bar used to pass hideWorkspaceLinks,
              // reasoning that a manager reaches their desk from their own bookmark
              // rather than from a club's shopfront; the owner's answer was that a person
              // who can manage should find the door in the same place on every page.
              <PublicAccountMenu />
            ) : (
              <Link to={signInHref} className={storefront.signInLink}>
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* The club's own drawer: the app list, the club's name in the head, and nothing
          of ours. Mounted for every viewer — `navOnly` is what keeps a stranger's copy
          identical to a member's, so there is no second drawer and no second rule. */}
      <AppDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        returnFocusRef={hamburgerRef}
        brand={{ label: club.name, to: clubHref }}
        navOnly
      />
    </>
  )
}
